<script lang="ts">
  // Substantiation Index per INTERFACE-SPECIFICATION.md §10.3.
  //
  // Groups every catalogue's _governance.substantiation_source by
  // its source URL or path and surfaces stale / orphaned entries.

  import { onMount } from 'svelte';
  import { bootstrapManifest, catalogueState, fetchCatalogue } from '../../lib/catalogue-store.svelte';
  import { PRODUCTS } from '../../lib/products';
  import { formatLocalDate } from '../../lib/formatters';

  onMount(async () => {
    if (!catalogueState.manifest) await bootstrapManifest();
    await Promise.all(PRODUCTS.map((p) => fetchCatalogue(p.jurisdiction, p.product)));
  });

  interface IndexRow {
    source: string;
    product_label: string;
    substantiation_date: string | null;
    is_orphan: boolean;
  }

  const rows = $derived(buildIndex());

  function buildIndex(): IndexRow[] {
    const out: IndexRow[] = [];
    for (const p of PRODUCTS) {
      const cube = catalogueState.catalogues[p.jurisdiction + '/' + p.product]?.catalogue;
      if (!cube) continue;
      const gov = (cube.product as { _governance?: { substantiation_source?: string; substantiation_date?: string } })._governance;
      const source = gov?.substantiation_source;
      out.push({
        source: source ?? '(missing)',
        product_label: p.label,
        substantiation_date: gov?.substantiation_date ?? null,
        is_orphan: !source
      });
    }
    return out.sort((a, b) => a.source.localeCompare(b.source));
  }
</script>

<section>
  <h3 class="section-heading">Substantiation index</h3>
  <table class="cube-table">
    <thead>
      <tr>
        <th>Source</th>
        <th>Product</th>
        <th>Substantiated</th>
        <th>Status</th>
      </tr>
    </thead>
    <tbody>
      {#each rows as row (row.source + row.product_label)}
        <tr>
          <td class="source-cell">
            {#if row.source.startsWith('http')}
              <a href={row.source} target="_blank" rel="noopener" data-external>{row.source}</a>
            {:else}
              <code>{row.source}</code>
            {/if}
          </td>
          <td>{row.product_label}</td>
          <td class="muted">{formatLocalDate(row.substantiation_date)}</td>
          <td>
            {#if row.is_orphan}
              <span class="badge badge-fail">Orphan</span>
            {:else}
              <span class="badge badge-pass">Linked</span>
            {/if}
          </td>
        </tr>
      {/each}
    </tbody>
  </table>
  <p class="muted">Stale-substantiation detection (≥18 months since validation) ships with PR 5 once the audit-trail tool surfaces the validation dates on every value.</p>
</section>

<style>
  .source-cell { word-break: break-all; max-width: 36rem; }
  code { font-family: var(--font-mono); font-size: 0.9em; }
</style>
